import {
  signIn,
  signUp,
  signInWithGoogle,
  _updateProfile,
  _updateUserPassword,
  _reload,
  _logout,
  _onAuthStateChanged,
} from 'src/lib/firebase';
import { createContext, useEffect, useReducer } from 'react';
import SplashScreen from 'src/components/SplashScreen';

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_STATE_CHANGED': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: 'FirebaseAuth',
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  loginWithGoogle: () => Promise.resolve(),
  update: () => Promise.resolve(),
  updateUserPassword: () => Promise.resolve(),
  reload: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  /**
   * Login
   * @param {string} email - Email
   * @param {string} password - Password
   * @returns {Promise<void>}
   */
  const login = async (email, password) => {
    return await signIn(email, password);
  };

  /**
   * Register
   * @param {string} email - Email
   * @param {string} password - Password
   * @returns {Promise<void>}
   */
  const register = async (email, password) => {
    return await signUp(email, password);
  };

  /**
   * Login with Google
   * @returns {Promise<void>}
   */
  const loginWithGoogle = () => {
    return signInWithGoogle();
  };

  /**
   * Update profile
   * @param {Object} profile - Profile
   * @returns {Promise<void>}
   */
  const update = async profile => {
    return await _updateProfile(profile);
  };

  /**
   * Update user password
   * @param {string} password - Password
   * @returns {Promise<void>}
   */
  const updateUserPassword = async password => {
    return await _updateUserPassword(password);
  };

  /**
   * Reload
   * @returns {Promise<void>}
   */
  const reload = () => {
    return _reload();
  };

  /**
   * Logout
   * @returns {Promise<void>}
   */
  const logout = () => {
    return _logout();
  };

  /**
   * On auth state changed
   * @returns {Promise<void>}
   */
  useEffect(() => {
    const unsubscribe = _onAuthStateChanged(authUser => {
      if (authUser) {
        const user = authUser.toJSON();
        dispatch({
          type: 'AUTH_STATE_CHANGED',
          payload: {
            isAuthenticated: true,
            user: {
              id: user.uid,
              email: user.email,
              name: user.displayName,
              phoneNumber: user.phoneNumber,
              emailVerified: user.emailVerified,
              isAnonymous: user.isAnonymous,
              createdAt: user.createdAt,
              lastLoginAt: user.lastLoginAt,
            },
          },
        });
      } else {
        dispatch({
          type: 'AUTH_STATE_CHANGED',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    });

    return unsubscribe;
  }, [dispatch]);

  /**
   * Splash screen
   * @returns {React.ReactNode}
   */
  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'FirebaseAuth',
        login,
        register,
        loginWithGoogle,
        update,
        updateUserPassword,
        reload,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
